import { mapArticle } from '../API/ArticleApi';
import { mapStorylineDilemma } from '../API/StorylineApi';
import { Article } from './Article';
import { Book } from './Book';
import { StorylineDilemma } from './StorylineDilemma';

export class BookChapter {
  id: string;
  bookId: string;
  articleId: string;
  chapterNumber: number;
  article?: Article;
  book?: Book;
  nextChapterTeaser?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  dilemmas?: StorylineDilemma[];

  constructor(data: any = {}) {
    // First copy all base properties
    Object.assign(this, data);

    this.book = data.book ? new Book(data.book) : undefined;
    this.article = data.article
      ? mapArticle(data.article, !!data.article?.questions)
      : undefined;
    this.dilemmas = data.dilemmas?.map((dilemma: any) =>
      mapStorylineDilemma(dilemma),
    );

    // Transform dates exactly as in mapBookChapter
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
    this.deletedAt = data.deletedAt ? new Date(data.deletedAt) : undefined;
  }

  getNextChapter(): BookChapter | undefined {
    return this.book.chapters.find(
      (chapter) => chapter.chapterNumber === this.chapterNumber + 1,
    );
  }

  isGenerated(): boolean {
    return !!this.article?.generatedAt;
  }

  isMyTurn(userId: string): boolean {
    if (!this.book.isAmongBookAuthors(userId)) {
      return false;
    }

    if (!this.book.isCollaborative()) {
      return true;
    }

    return (
      this.canCreateNextChapter(userId) ||
      !this.isDilemmasForNextChapterCustomized()
    );
  }

  /**
   * Determines if the user can create the next chapter based on turn-taking rules
   */
  canCreateNextChapter(userId: string): boolean {
    // Check if user is an author
    if (!this.book.isAmongBookAuthors(userId)) {
      return false;
    }

    // If not collaborative, only creator can create chapters
    if (!this.book.isCollaborative()) {
      return this.book.createdByUserId === userId;
    }

    // If this one was created by the user, they can't create the next one
    if (this.article?.createdByUserId === userId) {
      return false;
    }

    return this.isDilemmasForNextChapterCustomized();
  }

  /**
   * Determines if a user can customize the dilemmas for this chapter
   * This is only possible in collaborative mode, and only for the user
   * who didn't create the chapter but will write the next one
   */
  canCustomizeDilemmasForNextChapter(userId: string): boolean {
    // If not in collaborative mode, no one can customize dilemmas
    if (!this.book.isCollaborative()) {
      return false;
    }

    // Check if user is an author of the book
    if (!this.book.isAmongBookAuthors(userId)) {
      return false;
    }

    // User can customize dilemmas if the last chapter was created by them
    if (this.article?.createdByUserId !== userId) {
      return false;
    }

    return true;
  }

  isDilemmasForNextChapterCustomized(): boolean {
    return this.dilemmas.every((dilemma) => dilemma.customizedAt);
  }

  isLastChapter(): boolean {
    return this.chapterNumber === this.book.numberOfChapters;
  }

  canReadChapter(userId: string): boolean {
    if (!this.isGenerated()) {
      return false;
    }

    if (!this.book.isCollaborative()) {
      return true;
    }

    return (
      this.canCreateNextChapter(userId) ||
      this.isDilemmasForNextChapterCustomized() ||
      this.canCustomizeDilemmasForNextChapter(userId)
    );
  }
}
